import { Band, Eyebrow, displayFont } from "./band-helpers";
import type { CustomerStoriesBlock } from "@/lib/storefront-types";

type CustomerStoriesSectionProps = {
  block: CustomerStoriesBlock;
};

export function CustomerStoriesSection({
  block,
}: CustomerStoriesSectionProps) {
  const isLight = block.appearance === "light";
  const isFrozen = block.appearance === "frozen";
  const isDark = !isLight;

  return (
    <Band
      className={
        isLight
          ? "border-y storefront-border-soft bg-[#f4f8f1] py-12 sm:py-16"
          : isFrozen
            ? "storefront-surface-frozen py-12 sm:py-16"
            : "border-y border-white/8 storefront-surface-dark py-12 sm:py-16"
      }
    >
      {block.eyebrow ? <Eyebrow inverse={isDark}>{block.eyebrow}</Eyebrow> : null}
      <h2
        className={`${displayFont.className} mt-3 text-[clamp(1.7rem,3.2vw,2.7rem)] font-bold leading-[1.1] tracking-tight ${
          isDark ? "text-white" : "storefront-text-heading"
        }`}
      >
        {block.headline}
      </h2>

      <div className="mt-10 grid gap-6 lg:grid-cols-3">
        {block.storyItems.map((story) => (
          <article
            key={`${story.name}-${story.context}`}
            className={`relative flex flex-col rounded-[1.6rem] px-5 py-6 ${
              isLight
                ? "border storefront-border-soft bg-white shadow-[0_20px_48px_-28px_rgba(0,39,0,0.18)]"
                : isFrozen
                  ? "storefront-border-primary-soft storefront-surface-frozen-soft border shadow-[0_20px_48px_-28px_rgba(0,0,0,0.34)] backdrop-blur-sm"
                  : "border border-white/10 bg-white/6 shadow-[0_20px_48px_-28px_rgba(0,0,0,0.34)] backdrop-blur-sm"
            }`}
          >
            <svg
              className={`mb-4 h-8 w-8 ${
                isLight ? "text-[rgba(var(--storefront-primary-rgb),0.2)]" : "text-[rgba(var(--storefront-primary-rgb),0.35)]"
              }`}
              viewBox="0 0 32 32"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M10 8c-3.3 0-6 2.7-6 6v2h4v-2c0-1.1.9-2 2-2h2V8h-2zm14 0c-3.3 0-6 2.7-6 6v2h4v-2c0-1.1.9-2 2-2h2V8h-2z" />
            </svg>
            <blockquote>
              <p
                className={`text-lg leading-8 ${
                  isDark ? "text-white/88" : "text-[#1a3d1f]"
                }`}
              >
                &ldquo;{story.quote}&rdquo;
              </p>
            </blockquote>
            <div
              className={`mt-auto border-t pt-5 ${
                isLight ? "storefront-border-soft" : isFrozen ? "storefront-border-primary-soft" : "border-white/10"
              }`}
            >
              <p
                className={`text-sm font-semibold uppercase tracking-[0.16em] ${
                  isLight ? "storefront-text-primary" : "storefront-text-primary-tint"
                }`}
              >
                {story.name}
              </p>
              <p
                className={`mt-1 text-sm ${
                  isDark ? "text-white/58" : "storefront-text-body"
                }`}
              >
                {story.context}
              </p>
            </div>
          </article>
        ))}
      </div>
    </Band>
  );
}
